/**
 * `ActiveSession` — patient-session state with structural session-
 * lifecycle discipline.
 *
 * **Doctrinal posture: NO SHT BACKDOOR (client side).** Per Tech
 * Spec §6.2.3 + Doctrine §8 + ADR-026 + scope memo §5.1, the
 * patient's cleartext key material — derived PMK, unwrapped KEK,
 * unwrapped DEKs — exists in active patient session memory only.
 *
 * **CryptoKey-based posture (post-PR #88 / Option A landed).**
 *
 * Session-resident keys are `CryptoKey` objects produced by the
 * Web Crypto runtime (via `EnvelopeCryptoAdapter` delegating to
 * `@shn/envelope-crypto`). Per the runtime contract:
 *
 *   - `unwrapKey({ extractable: false })` produces structurally
 *     non-extractable handles. The runtime refuses to surface the
 *     underlying bytes via any API path: `JSON.stringify`, fetch
 *     / serialize, `localStorage` / `sessionStorage` / `IndexedDB`.
 *   - `derivePMK` yields a non-extractable PMK; same protection.
 *
 * This is **stronger than the PR #39 Uint8Array-based posture**
 * the original `ActiveSession` was designed against. The runtime
 * structurally enforces the doctrinal property; engineering
 * discipline is no longer the load-bearing protection.
 *
 * **Implication for `endSession()`.** Drop the `CryptoKey`
 * reference and let GC reclaim. There are no raw bytes to
 * `zeroize` — the runtime never exposed them. The PR #39 design
 * called `zeroize(kekBytes)` defensively; that path was
 * meaningful when keys were `Uint8Array` material the session
 * directly held. With `CryptoKey`, drop-and-GC is sufficient and
 * structurally complete.
 *
 * The session retains an `onEnd` callback for app-shell cleanup
 * (subscription handles, persisted UI state, observer
 * notification) and an idempotent transition; the underlying
 * cryptographic-property protection moves to the Web Crypto
 * runtime layer.
 *
 * **JS memory hygiene caveat (preserved framing).** Per
 * `@shn/envelope-crypto` module commentary + the
 * `feedback_phg_client_active_session_crypto.md` memory: the
 * load-bearing protection is "not at rest at SHT," not "no
 * cleartext bytes anywhere in any process memory at any moment."
 * `extractable: false` enforces the not-at-rest commitment at the
 * runtime layer; framework persistence patterns simply cannot
 * serialize a non-extractable CryptoKey. Push back on framework-
 * level patterns that try to extend session beyond active-presence
 * justification (React Suspense persistence, RN background-state
 * caching, devtools snapshot helpers) — those would not break
 * the runtime guarantee but would extend the window of session
 * reference retention beyond what active-presence justifies.
 *
 * SCAFFOLD: minimal state surface. Production session lifecycle
 * (idle timeout per PFO §2.2 12-24h configurable; risk-tier step-
 * up per PFO §2.2 table; cross-tab session coordination;
 * revocation propagation per Tech Spec §9.10) lands at the
 * `AuthenticationFlow` orchestrator surface in this PR (item 2).
 */
export class ActiveSessionError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.name = 'ActiveSessionError';
        this.code = code;
    }
}
// ---------- Implementation ----------
export class ActiveSession {
    options;
    _state = 'started';
    kekId = undefined;
    kek = undefined;
    endCallbackInvoked = false;
    constructor(options) {
        this.options = options;
    }
    get state() {
        return this._state;
    }
    get subject() {
        return this.options.subject;
    }
    loadUnwrappedKek(opts) {
        if (this._state === 'ended') {
            throw new ActiveSessionError('session has ended; cannot load KEK', 'session-ended');
        }
        // Replacing the KEK simply drops the prior reference (GC
        // reclaims the prior CryptoKey). Defensive against rotation
        // mid-session; typical case is loading exactly once.
        this.kekId = opts.kekId;
        this.kek = opts.key;
    }
    requireKek() {
        if (this._state === 'ended') {
            throw new ActiveSessionError('session has ended; KEK is not available', 'session-ended');
        }
        if (this.kekId === undefined || this.kek === undefined) {
            throw new ActiveSessionError('KEK not loaded into session; call loadUnwrappedKek first', 'kek-not-loaded');
        }
        return { kekId: this.kekId, key: this.kek };
    }
    endSession() {
        if (this._state === 'ended') {
            return;
        }
        let callbackError = undefined;
        if (this.options.onEnd !== undefined && !this.endCallbackInvoked) {
            this.endCallbackInvoked = true;
            try {
                this.options.onEnd();
            }
            catch (e) {
                callbackError = e;
            }
        }
        // Drop the CryptoKey reference; runtime + GC reclaim the
        // underlying handle. No zeroize: the runtime never exposed
        // bytes (extractable: false on the unwrapped key per
        // EnvelopeCryptoAdapter.unwrapKey default).
        this.kek = undefined;
        this.kekId = undefined;
        this._state = 'ended';
        if (callbackError !== undefined) {
            throw callbackError;
        }
    }
}
//# sourceMappingURL=active-session.js.map